import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  TextField
} from "@mui/material";
import CustomButton from "../CustomButton/Button";
// Make sure to call `loadStripe` outside of a component's render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!)
  .then((stripe) => {
    console.log("Stripe loaded successfully:", stripe ? "Yes" : "No");
    return stripe;
  })
  .catch((error) => {
    console.error("Failed to load Stripe:", error);
    return null;
  });

interface StripeCardFormProps {
  onSuccess: (customerId: string, tokenId: string, email?: string) => void;
  onError: (error: string) => void;
  onCancel: () => void;
  loading: boolean;
  existingCustomerId?: string; // For existing customers
  businessEmail?: string; // Business email for new customers
  paymentType?: string;
}

interface CardFormData {
  cardholderName: string;
}

const CardForm = ({
  onSuccess,
  onError,
  onCancel,
  loading,
  existingCustomerId,
  businessEmail,
  paymentType
}: StripeCardFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState<CardFormData>({
    cardholderName: ""
  });
  const [cardError, setCardError] = useState<string | null>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      onError("Stripe has not loaded yet. Please try again.");
      return;
    }

    if (!formData.cardholderName.trim()) {
      onError("Please enter the cardholder name.");
      return;
    }

    setIsProcessing(true);
    setCardError(null);

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      onError("Card element not found");
      setIsProcessing(false);
      return;
    }

    try {
      // Create payment method with card details
      const { error, paymentMethod }: any = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: formData.cardholderName
        }
      });

      if (error) {
        console.error("[Stripe Error]", error);
        // Handle postal code validation errors more gracefully
        if (
          error.type === "validation_error" &&
          (error.code === "incomplete_zip" || error.code === "invalid_zip") &&
          error.message?.includes("postal code")
        ) {
          // Try to create payment method without strict postal code validation
          console.log(
            "Postal code validation failed, attempting without strict validation"
          );
          setCardError(
            "Please check your postal code format (4+ digits recommended)"
          );
        } else {
          setCardError(
            error.message ||
              "An error occurred while creating the payment method"
          );
        }
        setIsProcessing(false);
      } else {
        console.log("[PaymentMethod]", paymentMethod);

        // Pass the payment method and customer info to backend for proper handling
        if (existingCustomerId) {
          // For existing customers: Backend will attach payment method to existing customer
          console.log(
            "Existing customer - backend will attach payment method:",
            existingCustomerId
          );
          onSuccess(existingCustomerId, paymentMethod.id);
        } else {
          // For new customers: Backend will create customer and attach payment method
          console.log(
            "New customer - backend will create customer and attach payment method"
          );
          onSuccess("", paymentMethod.id, businessEmail); // Empty customer ID indicates new customer, pass email
        }
        setIsProcessing(false);
      }
    } catch (err) {
      console.error("[Unexpected Error]", err);
      onError("An unexpected error occurred. Please try again.");
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: "16px",
        color: "#424770",
        fontFamily: '"Montserrat", sans-serif',
        "::placeholder": {
          color: "#aab7c4"
        }
      },
      invalid: {
        color: "#9e2146",
        iconColor: "#9e2146"
      }
    },
    hidePostalCode: true, // Hide built-in postal code to avoid red validation
    disabled: isProcessing || loading
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
      {/* Cardholder Name Field */}
      <TextField
        fullWidth
        label="Cardholder Name"
        name="cardholderName"
        value={formData.cardholderName}
        onChange={handleInputChange}
        variant="outlined"
        disabled={isProcessing || loading}
        required
        sx={{ mb: 2 }}
        placeholder="John Doe"
      />

      {/* Stripe Card Element */}
      <Box
        sx={{
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          mb: 2,
          backgroundColor: "#fafafa",
          minHeight: "60px"
        }}
      >
        <CardElement
          options={cardElementOptions}
          onChange={(event) => {
            if (event.error) {
              // Don't show postal code incomplete error for 4+ digit codes
              if (
                event.error.type === "validation_error" &&
                event.error.code === "incomplete_zip" &&
                event.error.message?.includes("postal code is incomplete")
              ) {
                // Allow the form to proceed - we'll handle this in submission
                setCardError(null);
              } else {
                setCardError(event.error.message);
              }
            } else {
              setCardError(null);
            }
          }}
        />
      </Box>

      {/* Card Error Display */}
      {cardError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {cardError}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <CustomButton callBack={onCancel} disabled={isProcessing || loading}>
          Cancel
        </CustomButton>

        <CustomButton
          callBack={handleSubmit}
          disabled={
            !stripe ||
            isProcessing ||
            loading ||
            !formData.cardholderName.trim()
          }
        >
          {isProcessing ? (
            <CircularProgress size={20} />
          ) : existingCustomerId && paymentType?.toLowerCase() === "stripe" ? (
            "Update Card"
          ) : (
            "Add Card"
          )}
        </CustomButton>
      </Box>
    </Box>
  );
};

const StripeCardForm: React.FC<StripeCardFormProps> = (props) => {
  useEffect(() => {
    // Check if Stripe publishable key is available
    if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
      console.error("REACT_APP_STRIPE_PUBLISHABLE_KEY is not set");
      props.onError("Stripe configuration error. Please contact support.");
      return;
    }
  }, [props]);

  if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <Typography variant="body2" color="error">
          Stripe configuration error. Please contact support.
        </Typography>
      </Box>
    );
  }

  const elementsOptions = {
    locale: "auto" as const // Support international postal code formats
  };

  return (
    <Elements
      stripe={stripePromise}
      options={elementsOptions}
      key="stripe-elements"
    >
      <CardForm {...props} />
    </Elements>
  );
};

export default StripeCardForm;
